import { useNotification } from "@refinedev/core";
import { List } from "@refinedev/antd";
import { Button, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useState, type FC } from "react";
import { axiosInstance } from "../../api/client";
import type { SnippetDeployResult } from "../../types";
import { SNIPPET_TYPE_COLOR } from "./constants";
import { CodeDrawer } from "./_components/code-drawer";
import { DeployModal } from "./_components/deploy-modal";
import { DeployResultsModal } from "./_components/deploy-results-modal";
import { ProjectSelect } from "./_components/project-select";
import { TargetsModal } from "./_components/targets-modal";
import { useSelectedProjectIds } from "./_components/use-selected-project-ids";
import type { DeployResultRow, SnippetRow } from "./types";
import { useSnippetsData } from "./use-snippets-data";

dayjs.extend(relativeTime);

const { Text } = Typography;

export const SnippetList: FC = () => {
  const { open: notify } = useNotification();

  const { projects, rows, targetsBySnippet, loading, refetch } = useSnippetsData();
  const [selectedProjectIds] = useSelectedProjectIds();

  const [codeDrawer, setCodeDrawer] = useState<SnippetRow | null>(null);
  const [deployModal, setDeployModal] = useState<{ snippetIds: string[] } | null>(null);
  const [deploying, setDeploying] = useState(false);
  const [targetsModal, setTargetsModal] = useState<SnippetRow | null>(null);
  const [deployResults, setDeployResults] = useState<DeployResultRow[] | null>(null);

  const openDeploy = (snippetIds: string[]) => {
    setDeployModal({ snippetIds });
  };

  const environmentLabel = (environmentId: string): string => {
    for (const proj of projects) {
      const env = proj.environments.find((e) => e.id === environmentId);
      if (env) return `${proj.name} / ${env.name}`;
    }
    return environmentId;
  };

  const confirmDeploy = async (selectedEnvironmentIds: string[]) => {
    if (!deployModal) return;
    setDeploying(true);

    // One call per snippet, looped client-side: deploy is atomic per (snippet, environment) on
    // the api, "deploy all" is a console-side convenience, not a separate bulk endpoint.
    const results: DeployResultRow[] = [];
    for (const snippetId of deployModal.snippetIds) {
      const row = rows.find((r) => r.id === snippetId);
      if (!row) continue;
      const { data } = await axiosInstance.post<SnippetDeployResult[]>(
        `/projects/${row.projectId}/snippets/${snippetId}/deploy`,
        { targetEnvironmentIds: selectedEnvironmentIds }
      );
      results.push(
        ...data.map((result) => ({
          ...result,
          snippetId,
          snippetName: row.name,
          environmentLabel: environmentLabel(result.environmentId),
        }))
      );
    }

    setDeploying(false);
    setDeployModal(null);

    const failures = results.filter((r) => r.status === "failure");
    // A single flattened toast becomes unreadable once more than one snippet/environment is
    // involved (which "deploy all" always is) — a dedicated results view keeps each failure
    // attributed to the snippet and target it actually happened on.
    if (results.length > 1 || failures.length > 0) {
      setDeployResults(results);
    } else {
      notify?.({ type: "success", message: "Deployed successfully" });
    }

    void refetch();
  };

  const removeTarget = async (row: SnippetRow, environmentId: string, removeFromSite: boolean) => {
    await axiosInstance.delete(
      `/projects/${row.projectId}/snippets/${row.id}/deployments/${environmentId}`,
      { data: { removeFromSite } }
    );
    void refetch();
  };

  const filteredRows =
    selectedProjectIds.length === 0 ? rows : rows.filter((row) => selectedProjectIds.includes(row.projectId));
  const activeFilteredRows = filteredRows.filter((r) => !r.deletedAt);

  return (
    <List title="Snippets" canCreate={false}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button
          type="primary"
          disabled={activeFilteredRows.length === 0}
          onClick={() => openDeploy(activeFilteredRows.map((r) => r.id))}
        >
          Deploy all
        </Button>
        <ProjectSelect projects={projects} />
      </Space>

      <Table<SnippetRow>
        dataSource={filteredRows}
        rowKey="id"
        loading={loading}
        scroll={{ x: "max-content" }}
        pagination={filteredRows.length > 10 ? { pageSize: 10 } : false}
        locale={{
          emptyText: (
            <div style={{ padding: "32px 0" }}>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>No published snippets yet</p>
              <p style={{ color: "#888", margin: 0 }}>
                Run <Typography.Text code>lps snippet publish</Typography.Text> from a project to share its
                snippets here.
              </p>
            </div>
          ),
        }}
      >
        <Table.Column<SnippetRow>
          title="Project"
          render={(_, row) => row.projectName}
        />
        <Table.Column<SnippetRow>
          title="Name"
          render={(_, row) => (
            <Space>
              {row.name}
              {row.deletedAt && (
                <Tag color="orange">
                  Removed from repo, still on {targetsBySnippet[row.id]?.length ?? 0} site(s)
                </Tag>
              )}
            </Space>
          )}
        />
        <Table.Column<SnippetRow>
          title="Type"
          render={(_, row) => <Tag color={SNIPPET_TYPE_COLOR[row.type]}>{row.type}</Tag>}
        />
        <Table.Column<SnippetRow>
          title="Status"
          render={(_, row) => {
            const targets = targetsBySnippet[row.id] ?? [];
            if (targets.length === 0) return <Text type="secondary">Never deployed</Text>;
            const upToDate = targets.filter((t) => t.upToDate).length;
            return <Text>{upToDate}/{targets.length} up to date</Text>;
          }}
        />
        <Table.Column<SnippetRow>
          title="Last updated"
          render={(_, row) => dayjs(row.updatedAt).fromNow()}
        />
        <Table.Column<SnippetRow>
          title=""
          align="right"
          render={(_, row) => (
            <Space size="small">
              <Button size="small" onClick={() => setCodeDrawer(row)}>
                View code
              </Button>
              {!row.deletedAt && (
                <Button size="small" onClick={() => openDeploy([row.id])}>
                  Deploy
                </Button>
              )}
              {(targetsBySnippet[row.id]?.length ?? 0) > 0 && (
                <Button size="small" onClick={() => setTargetsModal(row)}>
                  {row.deletedAt ? "Clean up" : "Manage targets"}
                </Button>
              )}
            </Space>
          )}
        />
      </Table>

      <CodeDrawer snippet={codeDrawer} onClose={() => setCodeDrawer(null)} />

      <DeployModal
        deployModal={deployModal}
        rows={rows}
        projects={projects}
        targetsBySnippet={targetsBySnippet}
        deploying={deploying}
        onCancel={() => setDeployModal(null)}
        onConfirm={(environmentIds) => void confirmDeploy(environmentIds)}
      />

      <TargetsModal
        snippet={targetsModal}
        targets={targetsModal ? targetsBySnippet[targetsModal.id] ?? [] : []}
        onClose={() => setTargetsModal(null)}
        onRemoveTarget={(environmentId, removeFromSite) => {
          if (targetsModal) void removeTarget(targetsModal, environmentId, removeFromSite);
        }}
      />

      <DeployResultsModal results={deployResults} onClose={() => setDeployResults(null)} />
    </List>
  );
};
